import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Home, Pause, Play, Volume2, VolumeX } from 'lucide-react';
import { IconButton, PrimaryButton, TertiaryButton } from '../components/brand/BrandButton';
import { Heading, BodyText, SmallText } from '../components/brand/BrandTypography';
import AudioTip from '../components/brand/AudioTip';

// Audio context for generating tones
const createAudioContext = () => {
    if (typeof window !== 'undefined') {
        return new (window.AudioContext || window.webkitAudioContext)();
    }
    return null;
};

/**
 * Calm Cycle
 * 
 * The orb expands and contracts with smooth easing, accelerating gently
 * into the inhale and slowing softly into the exhale. The motion is
 * continuous with no pauses or abrupt direction changes.
 * 
 * - Size change is subtle (5-8%)
 * - Varies slightly each cycle (1-3%) for organic feel
 * - Internal haze drifts continuously without resetting
 * - Background depth shifts blend smoothly with no steps or jumps
 */

// Theta-paced breath duration (seconds)
const BASE_BREATH_DURATION = 10;

// Session duration (2 minutes 30 seconds)
const SESSION_DURATION = 150;

// Continuous smooth easing - no pauses, no abrupt changes
// Uses sine wave for perfectly continuous motion
const continuousBreathEase = (t) => {
    // Sine wave creates natural acceleration into peak and deceleration out
    // No flat spots, no discontinuities
    return (1 - Math.cos(t * Math.PI * 2)) / 2;
};

// Main breathing orb with subtle, organic motion
const BreathingOrb = ({ breathPhase, swayOffset, cycleVariation, hazeState }) => {
    // Subtle size change: 5-8% expansion with 1-3% variation per cycle
    const baseExpansion = 0.065 + cycleVariation * 0.015; // 5-8% range
    const sizeMultiplier = 1 + breathPhase * baseExpansion;
    
    const baseSize = 290;
    const orbSize = baseSize * sizeMultiplier;
    const orbSizeStyle = `min(${54 * sizeMultiplier}vw, ${orbSize}px)`;
    
    // Haze responds to breath with organic variation
    // Central expansion on inhale, gentle contraction on exhale
    const hazeScale = hazeState.expansion;
    const hazeVariation = hazeState.variation;
    
    // Haze density - slightly brighter on inhale
    const hazeDensity = 0.55 + breathPhase * 0.15;

    return (
        <motion.div
            className="relative flex items-center justify-center"
            style={{
                x: swayOffset.x,
                y: swayOffset.y,
            }}
        >
            {/* Outer atmospheric halo */}
            <div
                className="absolute rounded-full"
                style={{
                    width: `min(88vw, 490px)`,
                    height: `min(88vw, 490px)`,
                    background: `radial-gradient(circle, 
                        rgba(212, 168, 77, ${0.05 + breathPhase * 0.025}) 0%, 
                        rgba(180, 140, 60, ${0.025 + breathPhase * 0.015}) 40%, 
                        transparent 70%
                    )`,
                    filter: 'blur(45px)',
                }}
            />

            {/* Secondary halo */}
            <div
                className="absolute rounded-full"
                style={{
                    width: `min(78vw, 420px)`,
                    height: `min(78vw, 420px)`,
                    background: `radial-gradient(circle, 
                        rgba(232, 197, 90, ${0.03 + breathPhase * 0.02}) 0%, 
                        rgba(200, 160, 70, ${0.015 + breathPhase * 0.01}) 50%, 
                        transparent 75%
                    )`,
                    filter: 'blur(35px)',
                }}
            />

            {/* Main orb body */}
            <div
                className="relative rounded-full overflow-hidden"
                style={{
                    width: orbSizeStyle,
                    height: orbSizeStyle,
                    boxShadow: `
                        0 0 ${50 + breathPhase * 25}px rgba(245, 216, 122, ${0.25 + breathPhase * 0.1}),
                        0 0 ${100 + breathPhase * 40}px rgba(212, 168, 77, ${0.12 + breathPhase * 0.06}),
                        0 0 ${160 + breathPhase * 55}px rgba(180, 140, 60, ${0.06 + breathPhase * 0.03}),
                        inset 0 0 ${70 + breathPhase * 25}px rgba(255, 250, 230, ${0.06 + breathPhase * 0.03})
                    `,
                    transition: 'width 0.1s linear, height 0.1s linear',
                }}
            >
                {/* Base golden fill */}
                <div 
                    className="absolute inset-0 rounded-full"
                    style={{
                        background: `radial-gradient(circle at 48% 48%, 
                            rgba(255, 245, 210, ${0.6 + breathPhase * 0.12}) 0%, 
                            rgba(245, 216, 122, ${0.52 + breathPhase * 0.1}) 25%, 
                            rgba(212, 168, 77, ${0.44 + breathPhase * 0.08}) 50%, 
                            rgba(180, 140, 60, ${0.36 + breathPhase * 0.06}) 75%,
                            rgba(150, 115, 45, ${0.28 + breathPhase * 0.04}) 100%
                        )`,
                    }}
                />

                {/* Internal haze - primary layer
                    Stays central, expands gently outward on inhale
                    Uses single centered gradient to avoid turbulence */}
                <div
                    className="absolute rounded-full"
                    style={{
                        // Centered inset that expands on inhale (smaller inset = larger visible area)
                        inset: `${12 - hazeScale * 8 - hazeVariation * 0.5}%`,
                        background: `radial-gradient(ellipse ${70 + hazeScale * 8 + hazeVariation}% ${65 + hazeScale * 6 + hazeVariation * 0.8}% at 50% 50%, 
                            rgba(255, 250, 225, ${hazeDensity * 0.22}) 0%, 
                            rgba(255, 245, 210, ${hazeDensity * 0.15}) 35%,
                            transparent 70%
                        )`,
                        filter: 'blur(16px)',
                        transition: 'inset 0.5s ease-out, background 0.5s ease-out',
                    }}
                />

                {/* Internal haze - secondary layer
                    Slightly delayed response for organic feel */}
                <div
                    className="absolute rounded-full"
                    style={{
                        inset: `${16 - hazeScale * 6 - hazeVariation * 0.3}%`,
                        background: `radial-gradient(ellipse ${60 + hazeScale * 10 + hazeVariation * 0.7}% ${70 + hazeScale * 8 + hazeVariation * 0.6}% at 50% 50%, 
                            rgba(255, 248, 218, ${hazeDensity * 0.18}) 0%, 
                            rgba(250, 238, 195, ${hazeDensity * 0.12}) 40%,
                            transparent 65%
                        )`,
                        filter: 'blur(14px)',
                        transition: 'inset 0.7s ease-out, background 0.7s ease-out',
                    }}
                />

                {/* Internal haze - deepest layer
                    Slowest response, creates depth illusion */}
                <div
                    className="absolute rounded-full"
                    style={{
                        inset: `${20 - hazeScale * 4 - hazeVariation * 0.2}%`,
                        background: `radial-gradient(ellipse ${75 + hazeScale * 6 + hazeVariation * 0.5}% ${80 + hazeScale * 5 + hazeVariation * 0.4}% at 50% 50%, 
                            rgba(255, 252, 235, ${hazeDensity * 0.12}) 0%, 
                            rgba(250, 242, 205, ${hazeDensity * 0.08}) 45%,
                            transparent 60%
                        )`,
                        filter: 'blur(20px)',
                        transition: 'inset 0.9s ease-out, background 0.9s ease-out',
                    }}
                />

                {/* Depth illusion - darkening toward edges */}
                <div 
                    className="absolute inset-0 rounded-full"
                    style={{
                        background: `radial-gradient(circle at 50% 50%, 
                            transparent 0%, 
                            transparent 30%,
                            rgba(180, 140, 60, ${0.04 + breathPhase * 0.02}) 60%,
                            rgba(150, 110, 40, ${0.065 + breathPhase * 0.03}) 80%,
                            rgba(120, 90, 30, ${0.085 + breathPhase * 0.035}) 100%
                        )`,
                    }}
                />

                {/* Inner glow - subtle highlight */}
                <div
                    className="absolute inset-0 rounded-full"
                    style={{
                        background: `radial-gradient(circle at 44% 44%, 
                            rgba(255, 255, 250, ${0.06 + breathPhase * 0.035}) 0%, 
                            transparent 32%
                        )`,
                    }}
                />

                {/* Soft edge dissolution */}
                <div 
                    className="absolute inset-0 rounded-full"
                    style={{
                        background: `radial-gradient(circle, 
                            transparent 72%, 
                            rgba(26, 24, 20, ${0.12 - breathPhase * 0.025}) 90%,
                            rgba(26, 24, 20, ${0.24 - breathPhase * 0.05}) 100%
                        )`,
                    }}
                />
            </div>
        </motion.div>
    );
};

// Atmospheric background with smooth blending depth shifts
const AtmosphericBackground = ({ smoothDepth, hazeState }) => {
    const hazeExpansion = hazeState.expansion;
    
    return (
        <div className="fixed inset-0 overflow-hidden">
            {/* Base warm dark */}
            <div 
                className="absolute inset-0"
                style={{
                    background: `rgb(${26 - smoothDepth * 1.2}, ${24 - smoothDepth * 1.2}, ${20 - smoothDepth * 0.8})`,
                }}
            />

            {/* Breathing atmospheric haze - expands/contracts with breath */}
            <div
                className="absolute inset-0"
                style={{
                    background: `radial-gradient(ellipse ${120 + hazeExpansion * 10}% ${100 + hazeExpansion * 8}% at 50% 50%, 
                        rgba(212, 168, 77, ${0.025 + smoothDepth * 0.01}) 0%, 
                        rgba(180, 140, 60, ${0.016 + smoothDepth * 0.006}) 40%,
                        transparent 70%
                    )`,
                }}
            />

            {/* Background haze - follows breath expansion */}
            <div
                className="absolute"
                style={{
                    inset: `${-20 - hazeExpansion * 5}%`,
                    background: `
                        radial-gradient(ellipse 60% 40% at 50% 50%, rgba(200, 160, 70, ${0.016 + smoothDepth * 0.006}) 0%, transparent ${50 + hazeExpansion * 3}%),
                        radial-gradient(ellipse 50% 60% at 50% 50%, rgba(190, 150, 65, ${0.012 + smoothDepth * 0.005}) 0%, transparent ${45 + hazeExpansion * 2}%),
                        radial-gradient(ellipse 70% 50% at 50% 50%, rgba(180, 140, 60, ${0.014 + smoothDepth * 0.006}) 0%, transparent ${55 + hazeExpansion * 2.5}%)
                    `,
                    filter: 'blur(65px)',
                }}
            />

            {/* Theta presence */}
            <div
                className="absolute inset-0"
                style={{
                    background: `radial-gradient(circle at 50% 50%, 
                        rgba(232, 197, 90, ${0.007 + smoothDepth * 0.005}) 0%, 
                        transparent 60%
                    )`,
                }}
            />

            {/* Vignette */}
            <div 
                className="absolute inset-0"
                style={{
                    background: `radial-gradient(circle at 50% 50%, 
                        transparent 40%, 
                        rgba(20, 18, 14, ${0.26 + smoothDepth * 0.03}) 80%,
                        rgba(15, 13, 10, ${0.46 + smoothDepth * 0.05}) 100%
                    )`,
                }}
            />
        </div>
    );
};

const BreathingOrbMeditation = () => {
    const navigate = useNavigate();
    const [phase, setPhase] = useState('intro');
    const [isPlaying, setIsPlaying] = useState(false);
    const [isComplete, setIsComplete] = useState(false);
    const [breathPhase, setBreathPhase] = useState(0.5);
    const [smoothDepth, setSmoothDepth] = useState(0.5);
    const [swayOffset, setSwayOffset] = useState({ x: 0, y: 0 });
    const [elapsedTime, setElapsedTime] = useState(0);
    
    // Audio state - OFF by default for ADHD sensory sensitivity
    const [soundEnabled, setSoundEnabled] = useState(false);
    const audioContextRef = useRef(null);
    const oscillatorRef = useRef(null);
    const gainNodeRef = useRef(null);
    
    // Cycle variation (1-3% per cycle) - use refs for targets, smooth interpolation
    const [cycleVariation, setCycleVariation] = useState(0);
    const [durationVariation, setDurationVariation] = useState(0);
    
    // Haze state with smooth expansion and organic variation
    const [hazeState, setHazeState] = useState({
        expansion: 0,      // 0 = contracted (exhale), 1 = expanded (inhale)
        variation: 0       // Small random variation per cycle for organic feel
    });
    
    const startTimeRef = useRef(null);
    const animationStartRef = useRef(null);
    const lastCycleRef = useRef(-1);
    const hazeVariationTargetRef = useRef(0);
    
    // Targets for smooth interpolation - prevents jolts at cycle boundaries
    const cycleVariationTargetRef = useRef(0);
    const durationVariationTargetRef = useRef(0);
    
    // Initialize audio system
    const initAudio = useCallback(() => {
        if (!audioContextRef.current) {
            audioContextRef.current = createAudioContext();
        }
        if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
            audioContextRef.current.resume();
        }
    }, []);
    
    // Create/update breathing tone - pitch rises on inhale, falls on exhale
    const updateBreathingTone = useCallback((breathValue) => {
        if (!soundEnabled || !audioContextRef.current) return;
        
        const ctx = audioContextRef.current;
        
        // Create oscillator if needed
        if (!oscillatorRef.current) {
            oscillatorRef.current = ctx.createOscillator();
            gainNodeRef.current = ctx.createGain();
            
            oscillatorRef.current.type = 'sine';
            oscillatorRef.current.connect(gainNodeRef.current);
            gainNodeRef.current.connect(ctx.destination);
            
            // Start very quiet
            gainNodeRef.current.gain.setValueAtTime(0, ctx.currentTime);
            oscillatorRef.current.start();
        }
        
        // Gentle frequency range: 180Hz (exhale) to 280Hz (inhale) - calming low tones
        const baseFreq = 180;
        const freqRange = 100;
        const targetFreq = baseFreq + (breathValue * freqRange);
        
        // Smooth frequency transition
        oscillatorRef.current.frequency.setTargetAtTime(targetFreq, ctx.currentTime, 0.3);
        
        // Volume follows breath - louder on inhale, softer on exhale
        // Keep overall volume low (max 0.08) to be gentle
        const targetGain = 0.03 + (breathValue * 0.05);
        gainNodeRef.current.gain.setTargetAtTime(targetGain, ctx.currentTime, 0.2);
    }, [soundEnabled]);
    
    // Stop audio
    const stopAudio = useCallback(() => {
        if (gainNodeRef.current && audioContextRef.current) {
            gainNodeRef.current.gain.setTargetAtTime(0, audioContextRef.current.currentTime, 0.3);
        }
        setTimeout(() => {
            if (oscillatorRef.current) {
                try {
                    oscillatorRef.current.stop();
                } catch (e) { /* already stopped */ }
                oscillatorRef.current = null;
            }
        }, 500);
    }, []);
    
    // Toggle sound
    const toggleSound = useCallback(() => {
        if (!soundEnabled) {
            initAudio();
            setSoundEnabled(true);
        } else {
            stopAudio();
            setSoundEnabled(false);
        }
    }, [soundEnabled, initAudio, stopAudio]);
    
    // Update tone with breath phase
    useEffect(() => {
        if (isPlaying && soundEnabled) {
            updateBreathingTone(breathPhase);
        }
    }, [breathPhase, isPlaying, soundEnabled, updateBreathingTone]);
    
    // Clean up audio on unmount or session end
    useEffect(() => {
        return () => {
            stopAudio();
            if (audioContextRef.current) {
                audioContextRef.current.close();
                audioContextRef.current = null;
            }
        };
    }, [stopAudio]);

    // Continuous breathing cycle - no pauses, no abrupt changes
    useEffect(() => {
        if (!isPlaying) return;

        if (!animationStartRef.current) {
            animationStartRef.current = Date.now();
        }
        
        const breathCycle = () => {
            const now = Date.now();
            const elapsed = (now - animationStartRef.current) / 1000;
            
            // Use smoothed duration variation for cycle calculation
            const currentDuration = BASE_BREATH_DURATION + durationVariation;
            const cyclePosition = (elapsed % currentDuration) / currentDuration;
            
            // Track cycle changes for variation updates - set TARGETS, not values directly
            const currentCycle = Math.floor(elapsed / currentDuration);
            if (currentCycle !== lastCycleRef.current) {
                lastCycleRef.current = currentCycle;
                
                // Set new targets - these will be smoothly interpolated
                cycleVariationTargetRef.current = (Math.random() - 0.5) * 0.04;
                durationVariationTargetRef.current = (Math.random() - 0.5) * 0.5;
                hazeVariationTargetRef.current = (Math.random() - 0.5) * 3;
            }
            
            // Smoothly interpolate cycle variation - no jolts
            setCycleVariation(prev => prev + (cycleVariationTargetRef.current - prev) * 0.02);
            
            // Smoothly interpolate duration variation - very slow to avoid timing jumps
            setDurationVariation(prev => prev + (durationVariationTargetRef.current - prev) * 0.01);
            
            // Continuous smooth easing
            const newBreathPhase = continuousBreathEase(cyclePosition);
            setBreathPhase(newBreathPhase);
            
            // Haze follows breath with smooth, eased response
            setHazeState(prev => {
                const expansionTarget = newBreathPhase;
                const variationTarget = hazeVariationTargetRef.current;
                
                return {
                    expansion: prev.expansion + (expansionTarget - prev.expansion) * 0.08,
                    variation: prev.variation + (variationTarget - prev.variation) * 0.02
                };
            });
            
            // Smooth depth blending for background
            setSmoothDepth(prev => prev + (newBreathPhase - prev) * 0.08);
        };

        const interval = setInterval(breathCycle, 30);
        return () => clearInterval(interval);
    }, [isPlaying, durationVariation]);

    // Subtle sway
    useEffect(() => {
        if (!isPlaying) return;

        const sway = () => {
            const time = Date.now() / 1000;
            setSwayOffset({
                x: Math.sin(time * 0.1) * 3 + Math.sin(time * 0.06) * 1.5,
                y: Math.cos(time * 0.08) * 2.5 + Math.cos(time * 0.045) * 1.2,
            });
        };

        const interval = setInterval(sway, 80);
        return () => clearInterval(interval);
    }, [isPlaying, phase]);

    // Session timer
    useEffect(() => {
        if (phase !== 'meditation' || !isPlaying) return;

        if (!startTimeRef.current) {
            startTimeRef.current = Date.now() - (elapsedTime * 1000);
        }

        const timer = setInterval(() => {
            const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
            setElapsedTime(elapsed);
            
            if (elapsed >= SESSION_DURATION) {
                setIsComplete(true);
                setPhase('complete');
                setIsPlaying(false);
            }
        }, 1000);

        return () => clearInterval(timer);
    }, [phase, isPlaying, elapsedTime]);

    const handleBegin = () => {
        setPhase('meditation');
        setIsPlaying(true);
        startTimeRef.current = Date.now();
        animationStartRef.current = Date.now();
        lastCycleRef.current = -1;
        if (soundEnabled) {
            initAudio();
        }
    };

    const handlePause = () => {
        if (isPlaying) {
            setIsPlaying(false);
            if (soundEnabled && gainNodeRef.current && audioContextRef.current) {
                gainNodeRef.current.gain.setTargetAtTime(0, audioContextRef.current.currentTime, 0.2);
            }
        } else {
            startTimeRef.current = Date.now() - (elapsedTime * 1000);
            setIsPlaying(true);
            if (soundEnabled) {
                initAudio();
            }
        }
    };

    const handleFinishEarly = () => {
        setIsComplete(true);
        setPhase('complete');
        setIsPlaying(false);
        stopAudio();
    };

    const handleRestart = () => {
        setPhase('intro');
        setIsPlaying(false);
        setIsComplete(false);
        setElapsedTime(0);
        startTimeRef.current = null;
        animationStartRef.current = null;
        lastCycleRef.current = -1;
        setCycleVariation(0);
        setDurationVariation(0);
        setBreathPhase(0.5);
        setSmoothDepth(0.5);
        setHazeState({ expansion: 0, variation: 0 });
        hazeVariationTargetRef.current = 0;
        cycleVariationTargetRef.current = 0;
        durationVariationTargetRef.current = 0;
        stopAudio();
    };

    const progress = (elapsedTime / SESSION_DURATION) * 100;
    const remainingTime = Math.max(0, SESSION_DURATION - elapsedTime);
    const minutes = Math.floor(remainingTime / 60);
    const seconds = remainingTime % 60;

    return (
        <div className="min-h-screen flex flex-col relative overflow-hidden">
            <AtmosphericBackground smoothDepth={smoothDepth} hazeState={hazeState} />

            {/* Header */}
            <header className="sticky top-0 z-50 bg-transparent">
                <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
                    <IconButton 
                        onClick={() => navigate('/home?tab=regulation')}
                        className="text-[#7D8E95]/70 hover:text-[#FFBB98] hover:bg-[#1D6B6B]/40 backdrop-blur-sm"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </IconButton>
                    <SmallText className="text-[#B5A590]/80 font-medium">Calm Cycle</SmallText>
                    <IconButton 
                        onClick={() => navigate('/home?tab=regulation')}
                        className="text-[#7D8E95]/70 hover:text-[#FFBB98] hover:bg-[#1D6B6B]/40 backdrop-blur-sm"
                    >
                        <Home className="w-5 h-5" />
                    </IconButton>
                </div>
            </header>

            {/* Main content */}
            <main className="flex-1 flex flex-col items-center justify-center px-4 py-8 relative z-10">
                {/* Intro */}
                <AnimatePresence>
                    {phase === 'intro' && (
                        <motion.div
                            initial={{ opacity: 0, y: -20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ duration: 0.8 }}
                            className="text-center px-6 max-w-md mb-6"
                        >
                            <Heading as="h2" size="text-2xl" className="mb-4 text-[#FFBB98]/90">
                                Calm Cycle
                            </Heading>
                            <BodyText className="text-[#7D8E95]/80 mb-4 leading-relaxed">
                                A slow, steady rhythm to help your system settle.
                            </BodyText>
                            <SmallText className="text-[#8A7A6A]/70 block leading-relaxed">
                                Simply watch the light. Let your breath follow its rhythm, 
                                or just observe. There's nothing to do.
                            </SmallText>
                            
                            {/* Audio tip */}
                            <div className="mt-4">
                                <AudioTip variant="ambient" />
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Progress */}
                {phase === 'meditation' && (
                    <div className="w-full max-w-md mb-6">
                        <div className="h-0.5 bg-[#3A3530]/50 rounded-full overflow-hidden backdrop-blur-sm">
                            <motion.div 
                                className="h-full bg-gradient-to-r from-[#FFBB98]/60 to-[#FFBB98]/60"
                                style={{ width: `${progress}%` }}
                            />
                        </div>
                        <div className="flex justify-center mt-2">
                            <SmallText className="text-[#8A7A6A]/60">
                                {minutes}:{seconds.toString().padStart(2, '0')} remaining
                            </SmallText>
                        </div>
                    </div>
                )}

                {/* Orb */}
                <div className="relative flex items-center justify-center" style={{ minHeight: phase === 'intro' ? '280px' : '350px' }}>
                    <BreathingOrb 
                        breathPhase={breathPhase}
                        swayOffset={swayOffset}
                        cycleVariation={cycleVariation}
                        hazeState={hazeState}
                    />

                    {/* Completion */}
                    <AnimatePresence>
                        {isComplete && (
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ duration: 1.5 }}
                                className="absolute inset-0 flex items-center justify-center"
                            >
                                <motion.div 
                                    className="text-center px-6 max-w-sm"
                                    initial={{ scale: 0.95, opacity: 0 }}
                                    animate={{ scale: 1, opacity: 1 }}
                                    transition={{ delay: 0.5, duration: 1 }}
                                >
                                    <Heading as="h2" size="text-xl" className="mb-4 text-[#FFBB98]/90">
                                        You are calm.
                                    </Heading>
                                    <BodyText className="text-[#7D8E95]/70 mb-10">
                                        Carry this stillness with you.
                                    </BodyText>
                                    <div className="space-y-4">
                                        <PrimaryButton 
                                            onClick={() => navigate('/home?tab=regulation')}
                                            className="w-full bg-[#FFBB98]/90 hover:bg-[#FFBB98] text-[#1A1814] border-0"
                                        >
                                            Return Home
                                        </PrimaryButton>
                                        <button 
                                            onClick={handleRestart}
                                            className="text-sm text-[#7D8E95]/60 hover:text-[#C9B896] transition-colors"
                                        >
                                            Another cycle
                                        </button>
                                    </div>
                                </motion.div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>

                {/* Intro buttons */}
                <AnimatePresence>
                    {phase === 'intro' && (
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 20 }}
                            transition={{ duration: 0.8, delay: 0.3 }}
                            className="text-center mt-6 px-6 max-w-sm"
                        >
                            <div className="space-y-4">
                                <PrimaryButton 
                                    onClick={handleBegin}
                                    className="w-full bg-[#FFBB98]/90 hover:bg-[#FFBB98] text-[#1A1814] border-0"
                                >
                                    Begin
                                </PrimaryButton>
                                <TertiaryButton 
                                    onClick={() => navigate('/home?tab=regulation')}
                                    className="w-full text-[#7D8E95]/70 hover:text-[#C9B896]"
                                >
                                    Not Now
                                </TertiaryButton>
                            </div>
                            
                            <SmallText className="text-[#6A5A4A]/60 mt-6 text-xs block">
                                ~2.5 minutes
                            </SmallText>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Controls - Central location below orb */}
                {phase === 'meditation' && !isComplete && (
                    <motion.div 
                        className="flex flex-col items-center justify-center mt-8 gap-5"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 1 }}
                    >
                        {/* Main Controls Row */}
                        <div className="flex items-center gap-6">
                            {/* Sound toggle */}
                            <div className="flex flex-col items-center gap-1">
                                <button
                                    onClick={toggleSound}
                                    className={`w-12 h-12 rounded-full backdrop-blur-sm border transition-all duration-300 flex items-center justify-center ${
                                        soundEnabled 
                                            ? 'bg-[#FFBB98]/20 border-[#FFBB98]/40' 
                                            : 'bg-[#2A2520]/60 border-[#3A3530]/50 hover:border-white/20'
                                    }`}
                                    data-testid="sound-toggle-btn"
                                >
                                    {soundEnabled ? (
                                        <Volume2 className="w-5 h-5 text-[#FFBB98]" />
                                    ) : (
                                        <VolumeX className="w-5 h-5 text-[#7D8E95]/60" />
                                    )}
                                </button>
                                <SmallText className={`text-xs ${soundEnabled ? 'text-[#FFBB98]/70' : 'text-[#7D8E95]/40'}`}>
                                    {soundEnabled ? 'Sound On' : 'Sound Off'}
                                </SmallText>
                            </div>
                            
                            {/* Play/Pause - Larger central button */}
                            <div className="flex flex-col items-center gap-1">
                                <IconButton 
                                    onClick={handlePause}
                                    className="w-16 h-16 rounded-full bg-[#2A2520]/60 backdrop-blur-sm border border-[#3A3530]/50 hover:border-white/20 transition-all duration-300"
                                >
                                    {isPlaying ? (
                                        <Pause className="w-7 h-7 text-[#C9B896]/80" />
                                    ) : (
                                        <Play className="w-7 h-7 text-[#FFBB98]/80" />
                                    )}
                                </IconButton>
                                <SmallText className="text-[#7D8E95]/40 text-xs">
                                    {isPlaying ? 'Pause' : 'Resume'}
                                </SmallText>
                            </div>
                        </div>
                        
                        {soundEnabled && (
                            <SmallText className="text-[#8A7A6A]/50 text-xs">
                                Gentle tones follow your breath
                            </SmallText>
                        )}
                        
                        <button 
                            onClick={handleFinishEarly}
                            className="text-sm text-[#7D8E95]/50 hover:text-[#C9B896]/70 transition-colors"
                            data-testid="finish-early-btn"
                        >
                            I'm done
                        </button>
                    </motion.div>
                )}
            </main>
        </div>
    );
};

export default BreathingOrbMeditation;
